// ═══════════════════════════════════════════════════════════════════════════
// RUN 6: Body Text Styling (body-only — tables excluded)
// ═══════════════════════════════════════════════════════════════════════════

import { safeAlert, reportResult, paraDetail, RunDetail } from '../runs/common';
import { ensureRunStyles } from '../taskpane/styleEngine';
import { getConfig } from '../taskpane/configService';
import { isListLikeParagraph } from '../runs/listDetection';
import { detectFootnote } from '../runs/footnoteDetection';
import {
  isParaClaimed,
  isSourceActive,
  getCacheSize,
} from './structuralParaCache';


// ─────────────────────────────────────────────────────────────────────────
// Pure classifiers
// ─────────────────────────────────────────────────────────────────────────

function isNumberedHeading(text: string): boolean {
  if (!text?.trim()) return false;
  // S6353: use '+' instead of '{1,}'
  return /^(\d+(?:\.\d+)+\.?)\s+/.test(text.trim());
}

function looksLikeTableStyle(styleName: string): boolean {
  if (!styleName) return false;
  const s = styleName.toLowerCase().trim();
  if (s.startsWith('table of ')) return false;
  return /^table[\s:]/i.test(s);
}

function looksLikeStructuralContent(text: string): { match: boolean; reason: string } {
  if (!text?.trim()) return { match: false, reason: "" };
  const t     = text.trim();
  const clean = t.replace(/[\u200B-\u200D\uFEFF]/g, "");

  if (/^Figure\s+\d/i.test(clean))      return { match: true, reason: "looks like a figure caption"   };
  if (/^Table\s+\d/i.test(clean))       return { match: true, reason: "looks like a table caption"    };
  if (/^Appendix\s+[A-Z]/i.test(clean)) return { match: true, reason: "looks like an appendix header" };
  if (clean.includes('\t') && /\t[\s.]*\d+\s*$/.test(clean)) {
    return { match: true, reason: "looks like a TOC/LOF/LOT entry (tab + page number)" };
  }
  return { match: false, reason: "" };
}


// ─────────────────────────────────────────────────────────────────────────
// Shared types
// ─────────────────────────────────────────────────────────────────────────

interface StylePredicates {
  shouldExcludeStyle: (styleName: string) => boolean;
  shouldConvertStyle: (styleName: string) => boolean;
  targetStyle:        string;
}

interface RunCounters {
  totalChecked:         number;
  totalCorrected:       number;
  misplacedTableFixed:  number;
  tableParasSkipped:    number;
  listParasSkipped:     number;
  structuralSkipped:    number;
  cacheSkipped:         number;
  footnoteChainSkipped: number;
}

interface RunState {
  counters:            RunCounters;
  changedParas:        RunDetail[];
  pendingFootnoteIds:  Set<string>;
  anyCacheActive:      boolean;
}

function makeCounters(): RunCounters {
  return {
    totalChecked: 0, totalCorrected: 0, misplacedTableFixed: 0,
    tableParasSkipped: 0, listParasSkipped: 0, structuralSkipped: 0,
    cacheSkipped: 0, footnoteChainSkipped: 0,
  };
}


// ─────────────────────────────────────────────────────────────────────────
// Footnote-chain confirmation  (extracted to reduce complexity)
// ─────────────────────────────────────────────────────────────────────────

/** Returns true + reason when the previous paragraph confirms a footnote context. */
async function confirmFootnoteByPrev(
  prevPara:           Word.Paragraph,
  pendingFootnoteIds: Set<string>,
  context:            Word.RequestContext
): Promise<{ confirmed: boolean; reason: string }> {
  prevPara.inlinePictures.load("items");
  prevPara.parentTableOrNullObject.load("isNullObject");
  await context.sync();

  const prevStyleLower = (prevPara.style || "").toLowerCase();
  const prevId         = prevPara.uniqueLocalId;

  if (!prevPara.parentTableOrNullObject.isNullObject)  return { confirmed: true,  reason: "prev para is inside a table"     };
  if (prevPara.inlinePictures.items.length > 0)        return { confirmed: true,  reason: "prev para is a figure"           };
  if (pendingFootnoteIds.has(prevId))                  return { confirmed: true,  reason: "prev para is pending footnote"   };
  if (prevStyleLower.includes('footnote'))             return { confirmed: true,  reason: "prev para has footnote style"    };
  return { confirmed: false, reason: "" };
}


// ─────────────────────────────────────────────────────────────────────────
// Per-paragraph decision  (step 1-4 gates, extracted to reduce complexity)
// ─────────────────────────────────────────────────────────────────────────

/**
 * Gate 3: footnote chain detection (only when cache is cold).
 * Returns a ParaOutcome if the paragraph should be skipped, or null to continue.
 */
async function checkFootnoteGate(
  para:      Word.Paragraph,
  paraText:  string,
  uniqueId:  string,
  index:     number,
  allParas:  Word.ParagraphCollection,
  context:   Word.RequestContext,
  state:     RunState
): Promise<ParaOutcome | null> {
  if (state.anyCacheActive) return null;

  const footnoteCheck = await detectFootnote(para, paraText, context);
  if (!footnoteCheck.detected || index === 0) return null;

  const { confirmed, reason } = await confirmFootnoteByPrev(
    allParas.items[index - 1], state.pendingFootnoteIds, context
  );
  if (confirmed) {
    console.log(`[${index + 1}] FOOTNOTE-CHAIN-SKIP (${footnoteCheck.reason} — ${reason}): "${paraText.trim().substring(0, 50)}"`);
    state.pendingFootnoteIds.add(uniqueId);
    return "skip-footnote";
  }
  console.log(`[${index + 1}] FOOTNOTE-PATTERN-NO-CONTEXT (${footnoteCheck.reason}): treating as body text`);
  return null;
}

/** Outcome of evaluating one paragraph. */
type ParaOutcome = "skip-table" | "skip-early" | "skip-cache" | "skip-structural"
                | "skip-footnote" | "skip-list" | "convert" | "no-change";

async function evaluateParagraph(
  para:       Word.Paragraph,
  index:      number,
  allParas:   Word.ParagraphCollection,
  context:    Word.RequestContext,
  predicates: StylePredicates,
  state:      RunState
): Promise<ParaOutcome> {
  // Table check
  const parentTable = para.parentTableOrNullObject;
  parentTable.load("isNullObject");
  await context.sync();
  if (!parentTable.isNullObject) return "skip-table";

  const styleName = para.style;
  const paraText  = para.text;
  const uniqueId  = para.uniqueLocalId;

  if (!paraText?.trim())                         return "skip-early";
  if (predicates.shouldExcludeStyle(styleName))  return "skip-early";
  if (isNumberedHeading(paraText))               return "skip-early";

  // Gate 1: cache skip
  if (state.anyCacheActive && isParaClaimed(uniqueId)) {
    console.log(`[${index + 1}] CACHE-SKIP: "${paraText.trim().substring(0, 50)}"`);
    return "skip-cache";
  }

  // Gate 2: structural text-pattern skip
  const structural = looksLikeStructuralContent(paraText);
  if (structural.match) {
    console.log(`[${index + 1}] STRUCTURAL-SKIP (${structural.reason}): "${paraText.trim().substring(0, 50)}"`);
    return "skip-structural";
  }

  // Gate 3: footnote chain detection (only when cache is cold)
  const footnoteOutcome = await checkFootnoteGate(para, paraText, uniqueId, index, allParas, context, state);
  if (footnoteOutcome !== null) return footnoteOutcome;

  // Gate 4: list skip
  const listCheck = await isListLikeParagraph(para, context, paraText, styleName);
  if (listCheck.isList) {
    console.log(`[${index + 1}] LIST-SKIP (${listCheck.reason}): "${paraText.trim().substring(0, 50)}"`);
    return "skip-list";
  }

  // Gate 5: convert or no-change
  return predicates.shouldConvertStyle(styleName) ? "convert" : "no-change";
}


// ─────────────────────────────────────────────────────────────────────────
// Outcome → counter + side-effect
// ─────────────────────────────────────────────────────────────────────────

async function applyOutcome(
  outcome:    ParaOutcome,
  para:       Word.Paragraph,
  context:    Word.RequestContext,
  predicates: StylePredicates,
  state:      RunState
): Promise<void> {
  const { counters, changedParas } = state;
  const { targetStyle }            = predicates;
  const styleName = para.style;
  const paraText  = para.text;
  const uniqueId  = para.uniqueLocalId;

  switch (outcome) {
    case "skip-table":      counters.tableParasSkipped++;                                        break;
    case "skip-cache":      counters.cacheSkipped++;      counters.structuralSkipped++;           break;
    case "skip-structural": counters.structuralSkipped++;                                        break;
    case "skip-footnote":   counters.footnoteChainSkipped++; counters.structuralSkipped++;       break;
    case "skip-list":       counters.listParasSkipped++;                                         break;
    case "no-change":       /* nothing to count */                                               break;

    case "convert": {
      const isMisplacedTable = looksLikeTableStyle(styleName);
      const reasonSuffix     = isMisplacedTable
        ? "  (misplaced table-context style outside any table)" : "";
      console.log(`CONVERTING: "${styleName}" → "${targetStyle}"${reasonSuffix}`);

      para.style = targetStyle;
      await context.sync();

      counters.totalCorrected++;
      if (isMisplacedTable) counters.misplacedTableFixed++;
      changedParas.push(paraDetail(
        uniqueId, paraText,
        styleName + " → " + targetStyle + (isMisplacedTable ? " (misplaced table style)" : "")
      ));
      break;
    }

    case "skip-early": /* no counter */ break;
  }

  // totalChecked counts every para that made it past the table gate
  if (outcome !== "skip-table") counters.totalChecked++;
}


// ─────────────────────────────────────────────────────────────────────────
// Main run logic
// ─────────────────────────────────────────────────────────────────────────

async function runImpl(context: Word.RequestContext): Promise<void> {
  const cfg         = getConfig();
  const bCfg        = cfg.run6_bodyText;
  const targetStyle = bCfg.targetStyle;

  const predicates: StylePredicates = {
    targetStyle,
    shouldExcludeStyle: (styleName: string) => {
      if (!styleName) return false;
      if (looksLikeTableStyle(styleName)) return false;
      return bCfg.excludeStylePatterns.some(p => styleName.toLowerCase().includes(p));
    },
    shouldConvertStyle: (styleName: string) => {
      if (!styleName) return false;
      if (looksLikeTableStyle(styleName)) return true;
      return bCfg.convertStyles.some(cs => styleName.toLowerCase() === cs.toLowerCase());
    },
  };

  await ensureRunStyles(context, 6);
  context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;

  const figCacheActive   = isSourceActive('figureFootnote');
  const tableCacheActive = isSourceActive('tableFootnote');
  const anyCacheActive   = figCacheActive || tableCacheActive;

  console.log(`=== Body Text Style Checker (${cfg.clientName}) ===`);
  console.log(`[Run 6] Cache — figureFootnote: ${figCacheActive}, tableFootnote: ${tableCacheActive}, total IDs: ${getCacheSize()}`);
  if (!anyCacheActive) console.log(`[Run 6] Cache cold — footnote chain detection will run.`);

  const state: RunState = {
    counters:           makeCounters(),
    changedParas:       [],
    pendingFootnoteIds: new Set(),
    anyCacheActive,
  };

  const paragraphs = context.document.body.paragraphs;
  paragraphs.load("items");
  await context.sync();

  // S4138: for-of instead of for(let i...)  — batch-load pass
  for (const para of paragraphs.items) {
    para.load("style, text, uniqueLocalId");
  }
  await context.sync();

  console.log(`Total paragraphs to check: ${paragraphs.items.length}`);

  for (let i = 0; i < paragraphs.items.length; i++) {
    const outcome = await evaluateParagraph(
      paragraphs.items[i], i, paragraphs, context, predicates, state
    );
    await applyOutcome(outcome, paragraphs.items[i], context, predicates, state);
  }

  context.document.changeTrackingMode = Word.ChangeTrackingMode.off;
  await context.sync();

  const { counters, changedParas } = state;

  console.log("=".repeat(70));
  console.log(`Body paragraphs checked:             ${counters.totalChecked}`);
  console.log(`Table paragraphs skipped:            ${counters.tableParasSkipped}`);
  console.log(`Paragraphs corrected:                ${counters.totalCorrected}`);
  console.log("✓ Body text style check complete!");

  if (counters.totalCorrected > 0) {
    reportResult('Body Text Styling Complete', [
      { label: 'Body paragraphs checked',          count: counters.totalChecked    },
      { label: `Paragraphs corrected to ${targetStyle}`, count: counters.totalCorrected },
      ...changedParas,
    ]);
  } else {
    reportResult('Body Text Styling — no changes needed', [
      { label: 'Body paragraphs checked', count: counters.totalChecked },
    ]);
  }
}

export async function run6() {
  await Word.run(async (context) => {
    try {
      await runImpl(context);
    } catch (error) {
      console.error("Error in run6:", error);
      safeAlert("An error occurred during the body text style check.");
    }
  });
}